import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  useGetAccountSetting,
  useUpdateAccountSettings,
} from "../services/useGetSetMyAccountSettings";

import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import useMyStatus from "../services/useMyStatus";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

import {
  useQuery,
  useMutation,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import FirstRunEmailSettings from "./FirstRunEmailSettings";
const queryClient = new QueryClient();

const FirstRun: React.FC = () => {
  const { isAuthenticated } = useAuth0();
  const { data } = useMyStatus();
  const [firstRunPosition, setFirstRunPosition] = useState(0);
  const [showFirstRun, setShowFirstRun] = useState(true);
  const [canFinish, setCanFinish] = useState(false);
  const adminConsentUrl =
    "https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=ae2d453e-3db2-410a-966c-b229e6425534&scope=https://graph.microsoft.com/.default&redirect_uri=" +
    window.location.origin +
    "/adminconsent";

  const query = useQuery({
    queryKey: ["firstRunCheck"],
    queryFn: useGetAccountSetting(),
  });
  const mutation = useMutation({
    mutationFn: useUpdateAccountSettings(),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["firstRunCheck"] });
    },
  });

  const handleEmailSettingsChange = (value: boolean) => {
    setCanFinish(value);
    console.log("Email settings changed: ", value);
  };

  const Stage1 = () => {
    return (
      //todo: different wording based on  {data?.membershipType}
      <>
        <DialogTitle textAlign={"center"} fontSize={24} marginTop={5}>
          Your journey to efficient Microsoft 365 change management starts here.
        </DialogTitle>
        <Box
          alignItems={"center"}
          justifyContent={"center"}
          width={"100%"}
          marginBottom={5}
        >
          <Box textAlign={"center"} m={2}>
            <Paper
              elevation={0}
              sx={{
                padding: 2,
                alignContent: "center",
                textAlign: "center",
                lineHeight: 2,
              }}
            >
              {data?.membershipType.startsWith("ChangePilot VIPs") && (
                <p>
                  Welcome to ChangePilot. You are part of our VIP programme for
                  friends and partners of Empowering.Cloud and ChangePilot,
                  granting you full access to all features and services.
                </p>
              )}

              {data?.membershipType === "Trial Membership" && (
                <p>Welcome to your 6-week ChangePilot trial.</p>
              )}

              {(data?.membershipType === "Standard Membership" ||
                data?.membershipType === "Pro Membership") && (
                <p>Welcome to ChangePilot.</p>
              )}

              <p>
                ChangePilot supports Product and Service Owners in managing
                Microsoft 365 change. ChangePilot users benefit from:
              </p>
              <div className="container" style={{ textAlign: "center" }}>
                <ul style={{ display: "inline-block", textAlign: "left" }}>
                  <li>
                    Every Microsoft 365 change item reviewed and summarized by
                    M365 experts
                  </li>
                  <li>
                    Tenant connection to only receive items from your
                    organisation
                  </li>
                  <li>
                    Monthly Change Call - an MVP/MCM goes through each change in
                    detail. Also available on demand with service and item
                    chapter marks
                  </li>
                  <li>Configurable change summary email</li>
                  <li>Summary dashboards</li>
                </ul>
              </div>
              {data?.isTenantIdStored && (
                <>
                  <p>
                    Good news - someone in your organisation has already granted
                    us access to your organisation's Message Center, so all the
                    information you get from ChangePilot will be specific to
                    your Microsoft 365 environment.
                  </p>
                  <p>
                    Get started by{" "}
                    <strong> configuring your ChangePilot Email</strong>. Click
                    'Next' below to continue.
                  </p>
                </>
              )}
              {!data?.isTenantIdStored && (
                <p>
                  Get started with ChangePilot by{" "}
                  <strong>connecting your tenant</strong> and{" "}
                  <strong>configuring your ChangePilot Email</strong>. Click
                  'Next' below to continue.
                </p>
              )}
            </Paper>
          </Box>

          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                if (data?.isTenantIdStored) {
                  console.log("Tenant ID already stored");
                  setFirstRunPosition(2);
                } else {
                  setFirstRunPosition(1);
                }
              }}
            >
              Next
            </Button>
          </div>
        </Box>
      </>
    );
  };

  const Stage2 = () => {
    return (
      <>
        <DialogTitle textAlign={"center"} fontSize={24} marginTop={5}>
          Tenant Connection
        </DialogTitle>
        <Box
          alignItems={"center"}
          justifyContent={"center"}
          width={"100%"}
          marginBottom={5}
        >
          <Box textAlign={"center"} m={2}>
            <Paper
              elevation={0}
              sx={{
                padding: 2,
                alignContent: "center",
                textAlign: "center",
                lineHeight: 2,
              }}
            >
              <p>
                By giving ChangePilot read only access to your Message Center,
                you will see all messages specifically for your environment.
                Once connected, your ChangePilot Email will also include
                messages specific for your tenant. Microsoft target messages
                based on licensing, usage and your configuration.
              </p>
              <p>
                If you don’t connect a tenant, you will see the standard feed of
                Microsoft 365 Message Centre messages for typical tenants. This
                means you may see messages about Microsoft 365 products and
                services that you do not have licenses for or use, and will not
                see important messages sent specifically to your tenant that
                require action based on your tenant's specific configuration.
              </p>
              <p style={{ color: "red", textDecoration: "underline" }}>
                When you connect your tenant, everyone verified as part of your
                organisation will automatically see a tenant-specific feed and
                receive tenant-specific emails from ChangePilot.
              </p>
              <p>
                For more information about the read-only permissions required,
                see{" "}
                <a
                  href="https://changepilot.cloud/knowledge-base/what-permissions-does-changepilot-require-from-our-tenant-"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  What permissions does ChangePilot require from our tenant?
                </a>
              </p>
            </Paper>
          </Box>

          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <Stack direction={"row"} spacing={2} justifyContent="center">
              <Button
                href={adminConsentUrl}
                target="_blank"
                variant="contained"
                color="primary"
              >
                View permissions and connect your Microsoft 365 Tenant
              </Button>

              <Button variant="outlined" onClick={() => setFirstRunPosition(2)}>
                Connect your tenant later
              </Button>
              <Button variant="outlined" onClick={() => setFirstRunPosition(2)}>
                Next
              </Button>
            </Stack>
          </div>
        </Box>
      </>
    );
  };

  const Stage3 = () => {
    return (
      <>
        <DialogTitle textAlign={"center"} fontSize={24} marginTop={5}>
          Configure your ChangePilot Email
        </DialogTitle>
        <Box
          alignItems={"center"}
          justifyContent={"center"}
          width={"100%"}
          marginBottom={5}
        >
          <Box textAlign={"center"} m={2}>
            <Paper
              elevation={0}
              sx={{
                padding: 2,
                alignContent: "center",
                textAlign: "center",
                lineHeight: 2,
              }}
            >
              <p>
                Pick the Microsoft 365 products/services you care about to
                tailor your ChangePilot Email preferences.
              </p>
              <p>
                ChangePilot categorises every Message Center item as high,
                medium or low for both admin and user impact. Each week,
                ChangePilot will send you high and medium admin and user impact
                items for the service categories you select below.*{" "}
                {data?.membershipType === "Trial Membership" && (
                  <>
                    You will be invited to configure your ChangePilot Emails in
                    more detail later in the trial.
                  </>
                )}
                {(data?.membershipType === "Standard Membership" ||
                  data?.membershipType === "Pro Membership") && (
                  <>
                    You can configure your ChangePilot Email in more detail via
                    'Email Settings' once in the Portal.
                  </>
                )}
              </p>

              <p>
                Choose your service categories and select 'Finish' to complete
                your ChangePilot set up.
              </p>
              <FirstRunEmailSettings
                onEmailSettingsChange={handleEmailSettingsChange}
              />
              {/* <FirstRunEmailSettings /> */}
              <i>
                *For more information about ChangePilot service categories, see{" "}
                <a
                  href="https://changepilot.cloud/knowledge-base/what-is-a-service-category"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  What is a Service Category?
                </a>
              </i>
            </Paper>
          </Box>

          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <Button
              variant="contained"
              disabled={!canFinish}
              color="primary"
              onClick={() => {
                setShowFirstRun(false);
                mutation.mutate({
                  firstRun: true,
                  email: query.data?.email || "",
                  jobTitle: query.data?.jobTitle || "",
                  lastName: query.data?.lastName || "",
                  firstName: query.data?.firstName || "",
                  company: query.data?.company || "",
                });
              }}
            >
              Finish
            </Button>
          </div>
        </Box>
      </>
    );
  };

  // if (isAuthenticated) {
  //   return (
  //     <>
  //       <QueryClientProvider client={queryClient}>
  //         <FirstRunExperience />
  //       </QueryClientProvider>
  //     </>
  //   );
  // } else {
  //   return <></>;
  // }

  return (
    <>
      {isAuthenticated && query.data != null && !query.data.firstRun ? (
        <>
          <QueryClientProvider client={queryClient}>
            <Dialog open={showFirstRun} maxWidth="md" fullWidth={true}>
              {firstRunPosition === 0 && <Stage1 />}
              {firstRunPosition === 1 && <Stage2 />}
              {firstRunPosition === 2 && <Stage3 />}
            </Dialog>
          </QueryClientProvider>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default FirstRun;
